import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import useValidation from '../../../hooks/use-validation'
import { resetPassword } from '../../../api-client/identity'
import { APIErrors } from '../../../api-client/enums'
import { FormField } from '../../../components/inputs'
import { Button } from '../../../components/button'

const re = {
  email: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
}

const FormFields = { Email: 'email' }

export default function SendPasswordResetRequest({ onSubmit }) {
  const [state, setState] = useState({ email: '' })
  const [mappedServerErrors, setMappedServerErrors] = useState({})

  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)

  const { mutate, isLoading, error } = useMutation({
    mutationFn: resetPassword,
    onSuccess: onSubmit,
  })

  useEffect(() => setMappedServerErrors(mapServerErrors(error?.errors, state)), [error, state])

  const handlePasswordResetEmailRequest = (e) => {
    e.preventDefault()

    if (!isFormValid) showErrors()
    else mutate({ email: state.email })
  }

  return (
    <form className='wrapper' onSubmit={handlePasswordResetEmailRequest}>
      <h1 className='title'>Please enter your email!</h1>
      <p className='description'>Enter your registration email</p>
      {(errors.email ||
        (mappedServerErrors.email && mappedServerErrors.email.data === state.email)) && (
        <span className='error'>{errors.email || mappedServerErrors.email.message}</span>
      )}
      <FormField
        aria-label='email'
        type='email'
        className='formField'
        placeholder='Email'
        error={
          errors.email ||
          (mappedServerErrors.email && mappedServerErrors.email.data === state.email)
        }
        value={state.email}
        onChange={(e) => setState({ email: e.target.value })}
      />
      <p className='notice'>You'll receive the link within the email to reset your password.</p>
      {mappedServerErrors[APIErrors.Unknown] && (
        <p className='errorText'>{mappedServerErrors[APIErrors.Unknown].message}</p>
      )}
      <Button type='submit' disabled={isLoading} className='sendBtn'>
        Send
      </Button>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 2rem 4.1rem;
          background: #fff;
          border-radius: 1rem;

          :global(.sendBtn) {
            display: block;
            margin: 0 auto;
          }

          :global(.formField) {
            width: 100%;
            margin: 1.3rem 0 2.6rem;
          }
        }

        .title {
          text-align: center;
          font-size: 3.2rem;
        }

        .error {
          margin: 0 0 1rem;
          color: var(--alert-color);
          font-size: 1.4rem;
        }

        .notice {
          margin: 0 0 1.4rem;
          text-align: center;
          line-height: 1.9rem;
          font-size: 1.4rem;
        }

        .description {
          text-align: center;
          padding: 2.5rem 0 1.4rem;
          line-height: 1.9rem;
          font-size: 1.6rem;
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 1.6rem 0 2.3rem;

            :global(.sendBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }
        }
      `}</style>
    </form>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  if (!re.email.test(state.email)) {
    errors[FormFields.Email] = 'Email is not in valid format'
    isValid = false
  }

  return { errors, isValid }
}

function mapServerErrors(errors, state) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (parseInt(error.code)) {
      case APIErrors.UserNotFound:
        mappedErrors[FormFields.Email] = { message: 'User Not Found', data: state.email }
        break
      case APIErrors.ValidationPending:
        mappedErrors[FormFields.Email] = {
          message: 'Email Validation pending, Contact Support.',
          data: state.email,
        }
        break
      default:
        mappedErrors[APIErrors.Unknown] = {
          message: 'Something went wrong, try again later.',
          data: state.email,
        }
        break
    }
  })

  return mappedErrors
}
